import React, { useMemo } from 'react';
import { Btn, DataTable, KPI, KPIRow, Loading, Msg, useAdminGet } from '../../kit';
import { AdminLink } from '../../adminNav';
import { Badge } from '../emailKit';
import { N, Pct, SECTION, arr, num, pctText, rateOf, shareHint, str } from './shared';

/**
 * Per campaign (email/stats_campaigns): every batch sent from Campaigns — and
 * every MEMAC "send to these N now", which goes through the same sender — in
 * the period, with its delivery and engagement rates (the Campaigns tab's own
 * funnel numbers, so the team's addresses count here as they do there); and
 * underneath, each researcher-outreach campaign. The full record (funnel,
 * content as sent, each recipient) stays in Campaigns; "Open" goes there.
 *
 * The open-rate KPI counts HTML campaigns only — a plain-text mail carries no
 * open pixel, so its deliveries would only drag the rate down. For the whole
 * period it is the server's total (totals.open_rate, HTML only); with the
 * template filter, the same sum over the HTML rows shown. null = "n/a".
 *
 * With a bulk group picked (2026-10-15) the server counts each campaign over
 * the group's members only, and says so in `notes`. Opened / clicked are
 * people's unless `automated` (the page's "Include automated"); auto opens and
 * bot clicks are their own columns either way.
 */

/** HTML (open-trackable) row: the server's flag, else "it has an open rate". */
const isHtml = (r: any): boolean => (typeof r?.html === 'boolean' ? r.html : r?.open_rate !== null);

const CampaignStats: React.FC<{
  range: { from?: string; to?: string }; template?: string; mode?: string; group?: string;
  tplName: (k: string) => string; stamp: number; automated?: boolean;
}> = ({ range, template, mode, group, tplName, stamp, automated }) => {
  const params: Record<string, string> = {};
  if (range.from) params.from = range.from;
  if (range.to) params.to = range.to;
  if (group) params.group = group;
  if (automated) params.automated = '1';
  const q = useAdminGet(SECTION, 'stats_campaigns', params, [JSON.stringify(params), stamp]);
  const d: any = q.data;
  const all: any[] = Array.isArray(d) ? d : arr(d?.campaigns ?? d?.rows ?? d?.batches);
  const outreach: any[] = arr(d?.outreach).filter(r => !template || str(r?.template_key) === template)
    .map(r => ({ ...r, open_rate: rateOf(r, 'open_rate'), click_rate: rateOf(r, 'click_rate') }));
  // The server reads only the period; the template filter above is applied
  // here so the table answers the same question as the rest of the page.
  const rows = useMemo(() => all
    .filter(r => !template || str(r.template_key) === template)
    .map(r => {
      const content = str(r.content_mode);
      return {
        ...r,
        campaign: str(r.name) || str(r.subject) || str(r.send_id),
        content: content === 'custom' ? 'Written for this send' : content === 'design' ? 'Design'
          : str(r.template_name) || tplName(str(r.template_key ?? r.template)),
        by: str(r.by ?? r.sent_by),
        recipients: r.recipients ?? r.queued,
        open_rate: rateOf(r, 'open_rate'),
        click_rate: rateOf(r, 'click_rate'),
        ctor: rateOf(r, 'ctor'),
      };
    }), [all, template, tplName]);
  const sum = (k: string, only?: (r: any) => boolean) =>
    rows.reduce((a, r) => a + (!only || only(r) ? num(r[k]) : 0), 0);
  const delivered = sum('delivered');
  // Every opened e-mail, automated ones too: in the people view the auto-only
  // ones are not in `opened`, so they are added back.
  const rawOpened = automated ? sum('opened') : sum('opened') + sum('auto_opened');
  const openRate = ((): number | null | undefined => {
    if (!delivered) return undefined;                       // nothing delivered: "—"
    const tot = d && !Array.isArray(d) ? d.totals : null;
    if (!template && tot && typeof tot === 'object' && 'open_rate' in tot) {
      if (tot.open_rate === null) return null;
      const v = Number(tot.open_rate);
      return Number.isFinite(v) ? v : undefined;
    }
    const htmlDelivered = sum('delivered', isHtml);
    return htmlDelivered ? sum('opened', isHtml) / htmlDelivered : null;
  })();

  if (q.loading && !q.data) return <Loading />;
  return (
    <div className="flex flex-col gap-3">
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {mode === 'auto' && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Campaigns are manual sends, so the "Automatic only" filter above does not apply to this table.
        </div>
      )}
      <KPIRow>
        <KPI label="Campaigns" value={rows.length.toLocaleString()} hint={template ? 'of this e-mail' : 'in this period'} />
        <KPI label="Sent" value={sum('sent').toLocaleString()} />
        <KPI label="Open rate" value={<Pct v={openRate} />}
          hint="opened or clicked ÷ delivered, HTML campaigns only (plain text has no open tracking)" />
        <KPI label="Click rate" value={delivered ? pctText(sum('clicked') / delivered) : '—'} hint="clicked ÷ delivered" />
        <KPI label="Instant / auto opens" value={sum('auto_opened').toLocaleString()}
          hint={shareHint(rawOpened ? sum('auto_opened') / rawOpened : null, 'opened e-mails had only automated opens')
            || 'opened only by machines'} />
        <KPI label="Bot clicks" value={sum('bot_clicked').toLocaleString()} hint="clicked only by link scanners" />
      </KPIRow>
      <DataTable rows={rows} csvName="email_campaign_stats" tableId="email_dash_campaigns" defaultSort="-created_at"
        maxHeight="36rem"
        columns={['created_at', 'campaign', 'content', 'by', 'recipients', 'sent', 'delivered', 'opened', 'clicked',
          'open_rate', 'click_rate', 'auto_opened', 'bot_clicked', 'trigger_id']}
        optionalColumns={['ctor', 'bounced', 'failed', 'visited', 'signed_up', 'trial', 'status', 'subject', 'send_id']}
        labels={{ created_at: 'Sent', trigger_id: 'MEMAC trigger', open_rate: 'Open rate', click_rate: 'Click rate',
          send_id: 'Send id', ctor: 'CTOR', signed_up: 'Signed up', trial: 'Trials', auto_opened: 'Auto opens',
          bot_clicked: 'Bot clicks' }}
        render={{
          campaign: r => <span className="font-medium" title={str(r.subject) || undefined}>{r.campaign}</span>,
          recipients: r => <N v={r.recipients} />,
          auto_opened: r => <N v={r.auto_opened} />,
          bot_clicked: r => <N v={r.bot_clicked} />,
          open_rate: r => <Pct v={r.open_rate} />,
          click_rate: r => <Pct v={r.click_rate} />,
          ctor: r => <Pct v={r.ctor} />,
          trigger_id: r => (r.trigger_id ? <Badge tone="blue">{r.trigger_id}</Badge> : <span className="text-grayscale-1100">—</span>),
        }}
        actions={r => (r.send_id ? (
          <AdminLink to={{ group: 'email', tab: 'campaigns', campaign: str(r.send_id) }}
            title="The whole record in Campaigns: funnel, content as sent, every recipient"
            className="text-xs text-primary-700 underline underline-offset-2 whitespace-nowrap">Open</AdminLink>
        ) : null)}
        empty={template ? 'No campaign sent this e-mail in this period.' : 'No campaign was sent in this period.'} />
      {outreach.length > 0 && (
        <>
          <div className="text-sm font-bold text-grayscale-1300 font-grotesk pt-2">Researcher outreach</div>
          <DataTable rows={outreach} csvName="email_outreach_stats" tableId="email_dash_outreach" defaultSort="-last_sent_at"
            maxHeight="24rem"
            columns={['last_sent_at', 'name', 'subject', 'sent', 'delivered', 'opened', 'clicked', 'open_rate', 'click_rate',
              'auto_opened', 'bot_clicked']}
            optionalColumns={['first_sent_at', 'campaign_id', 'failed', 'bounced']}
            labels={{ last_sent_at: 'Last sent', first_sent_at: 'First sent', name: 'Kind', campaign_id: 'Campaign id',
              open_rate: 'Open rate', click_rate: 'Click rate', auto_opened: 'Auto opens', bot_clicked: 'Bot clicks' }}
            render={{
              subject: r => <span className="text-xs text-grayscale-1200" title={str(r.subject)}>{str(r.subject) || '—'}</span>,
              open_rate: r => <Pct v={r.open_rate} />,
              click_rate: r => <Pct v={r.click_rate} />,
            }} />
        </>
      )}
      {d && !Array.isArray(d) && arr(d.notes).length > 0 && (
        <div className="text-[11px] text-grayscale-1100 flex flex-col gap-0.5">
          {arr(d.notes).map((n: any, i: number) => <span key={i}>{str(n)}</span>)}
        </div>
      )}
      <div className="flex items-center gap-2 text-[11px] text-grayscale-1100">
        <span className="flex-1">Opened and clicked come from SendGrid's events, matched to each recipient's own message
          {automated ? ', every open and click counted' : ', people only: auto opens (instant reads, Apple MPP, scanners) and bot clicks are counted apart'}.
          Sign-ups and trials after a campaign are in Campaigns → Campaigns.</span>
        <Btn small onClick={q.reload}>Refresh</Btn>
      </div>
    </div>
  );
};

export default CampaignStats;
